<template>
  <div class="relative text-xs @container">
    <div
      v-if="isOverflowing"
      class="flex items-center justify-center gap-2 px-4 py-1 rounded-t w-fit h-9 bg-blue-50 text-primary-900"
    >
      <p class="text-sm">{{ t('scroll_to_show_all_columns') }}</p>
      <CIcon name="multiple_stop" />
    </div>
    <table
      ref="variantsTable"
      data-test="variant-table"
      class="variant-table-grid-layout"
      :style="'--cols:' + sortedTableHeaders.length"
      :class="{ 'full-variant': isLoggedIn, 'no-cols': !sortedTableHeaders.length }"
    >
      <thead class="contents">
        <tr data-test="variant-table-header" class="w-full hidden @4xl:contents">
          <th
            data-test="variant-table-header-fixed-left"
            class="sticky left-0 px-2 flex gap-4 items-center bg-primary-50 z-[2] cursor-pointer"
            :class="{
              'shadow-[5px_5px_8px_0_rgba(0,0,0,0.08)]': isScrolled,
            }"
            @click="updateSortingConfig('attributes', artNrColumnHeader.name)"
          >
            <VariantsAttribute :value="artNrColumnHeader.label" />
            <CIcon class="text-gray-800 min-w-[12px] ml-2" name="unfold_more" />
          </th>
          <th
            v-for="attribute in sortedTableHeaders"
            :key="attribute.name"
            class="flex items-center gap-4 px-2 cursor-pointer bg-primary-50"
            @click="updateSortingConfig('attributes', attribute.name)"
          >
            <VariantsAttribute :value="attribute.label" />
            <CIcon class="text-gray-800 min-w-[12px] ml-2" name="unfold_more" />
          </th>
          <th class="flex items-center gap-4 px-2 bg-primary-50"></th>
          <th
            v-if="isLoggedIn"
            data-test="variant-table-header-fixed-right"
            class="sticky right-0 flex items-center gap-4 px-2 bg-primary-50"
            :class="{
              'shadow-[-5px_5px_8px_0_rgba(0,0,0,0.08)]': !isScrolledToEnd && isOverflowing,
            }"
          >
            <div
              v-if="centralLocationId"
              class="flex items-center justify-end w-16 py-0 pl-0 pr-1 cursor-pointer"
              @click="updateSortingConfig('centralStock')"
            >
              <CIcon class="text-gray-800" name="warehouse" title="Centrallager" />
              <CIcon class="text-gray-800 min-w-[12px] ml-2 mr-[6px]" name="unfold_more" />
            </div>
            <div
              v-if="getCurrentStockLocation"
              class="flex items-center justify-end w-16 py-0 pl-0 pr-1 cursor-pointer"
              @click="updateSortingConfig('storeStock')"
            >
              <CIcon class="text-gray-800" name="storefront" :title="getStoreName" />
              <CIcon class="text-gray-800 min-w-[12px] ml-2 mr-[6px]" name="unfold_more" />
            </div>
            <div class="flex items-center px-2 cursor-pointer whitespace-nowrap" @click="updateSortingConfig('price')">
              <span>{{ t('price.price') }}</span>
              <CIcon class="text-gray-800 min-w-[12px] ml-2" name="unfold_more" />
            </div>
          </th>
        </tr>
      </thead>
      <tbody class="contents">
        <VariantTableRow
          v-for="variant in sortedVariants"
          :key="variant.articleNumber"
          :layout="layout"
          :variant="variant"
          :table-headers="sortedTableHeaders"
          :common-variant-attributes="product.commonVariantAttributes"
          :selected-article-number="currentId"
          :is-wide-table="isWideTable"
          :is-scrolled="isScrolled"
          :is-scrolled-to-end="isScrolledToEnd"
          :is-overflowing="isOverflowing"
          :is-any-variant-discontinued="isAnyVariantDiscontinued"
          @open-image-modal="imageModalVariant = variant"
          @open-price-modal="openPriceModal(variant)"
          @open-replacement-modal="openReplacementModal(variant)"
          @open-stock-modal="setStockInformationData($event, variant)"
          @track-navigation="trackNavigation"
        />
      </tbody>
    </table>
    <div v-if="(product.variants?.length || 0) > limit" class="flex flex-col items-center w-full gap-2 p-4 @4xl:hidden">
      <p class="text-base text-gray-800">
        {{
          t('displayed_count', {
            count: previewLimit,
            total: product.variants?.length,
          })
        }}
      </p>
      <CButton variant="secondary" @click="showMoreVariants()">{{ t('show_more_variants') }}</CButton>
    </div>
    <VariantsOverlay
      class="hidden"
      :class="{ '@4xl:flex': isTableOverlayVisible }"
      :preview-limit="previewLimit"
      :num-variants="product.variants?.length"
      :has-scroll-bar="isOverflowing"
      @expand="expandVariants()"
    />

    <LazyImageModal v-if="imageModalVariant" :variant="imageModalVariant" @close="imageModalVariant = undefined" />
  </div>
  <LazyPriceAndDiscountModal
    v-if="isPriceAndDiscountModalVisible && selectedVariant"
    :is-discount-modal="false"
    :price="selectedVariant.price"
    :sales-units="selectedVariant.webSalesUnit"
    @close="closePriceAndDiscountModal"
  />

  <LazyReplacementModal
    v-if="discontinuedArticleNumber && selectedVariant"
    :discontinued-article-number="discontinuedArticleNumber"
    @close="closeReplacementModal"
  />

  <LazyStockInformationModal
    v-if="stockInformationData"
    :stock-information-data="stockInformationData"
    :is-purchasable="stockInformationData.isPurchasable"
    @close="setStockInformationData(null)"
  />
</template>

<script setup lang="ts">
import type { VNodeRef } from 'vue-demi';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import {
  useDeliveryStore,
  useTrackingStore,
  useUserStore,
  useBrandStore,
  useStoresStore,
  useProductStore,
} from '@/stores';
import type { Product, Variant, VariantTableHeader } from '@/types/products';
import { SIZE_DESCRIPTION_ATTRIBUTE_NAME, SUPPLIER_ITEM_NUMBER_ATTRIBUTE_NAME } from '~/utils/Products';

type SortVariant = Variant & { combinedAttrs?: any };

type Props = {
  layout: 'pdp' | 'plp';
  product: Product;
  currentId?: string;
};

const SHOW_MORE_NUM = 7;
const previewLimit = 10;

const props = withDefaults(defineProps<Props>(), {
  currentId: '',
});

const emit = defineEmits<{
  (e: 'variant-table-updated', variants: Variant[]): void;
  (e: 'track-navigation', articleNumber?: string): void;
  (e: 'open-image-modal', variant: Variant): void;
}>();

const { t } = useI18n();
const config = useRuntimeConfig();

const { isLoggedIn } = storeToRefs(useUserStore());
const { getCurrentStockLocation } = storeToRefs(useDeliveryStore());
const { sortSequence } = storeToRefs(useProductStore());
const { brandConfig } = useBrandStore();
const { visitProduct } = useTrackingStore();
const { getStore } = useStoresStore();

const isLoading = inject('isLoading', ref({ stock: false, price: false }));
const attributesToHide = inject('attributesToHide', []);

const { $tracking } = useNuxtApp();
const sortingId = ref('attributes-articleNumber');
const isSortAcsending = ref(true);
const sortBy = ref<any>(undefined);
const selectedVariant = ref<any>(null);
const isTrackingComplete = ref(false);
const isPriceAndDiscountModalVisible = ref(false);
const discontinuedArticleNumber = ref<string | undefined>(undefined);
const stockInformationData = ref<any>(null);
const variantsTable = ref<null | VNodeRef>(null);
const isExpanded = ref(false);
const isOverflowing = ref(false);
const isScrolled = ref(false);
const isScrolledToEnd = ref(false);
const limit = ref(10);
const imageModalVariant = ref<Variant | undefined>(undefined);
const artNrColumnHeader: VariantTableHeader = {
  label: 'Art. Nr.',
  name: 'articleNumber',
  sort: '0',
};

const variantNameAttribute = 'name';

const centralLocationId = computed(() => brandConfig.centralStorageId);
const hiddenAttributes = computed(() =>
  [
    t('synonyms'),
    t('supplier_art_nr'),
    SUPPLIER_ITEM_NUMBER_ATTRIBUTE_NAME,
    t('reach_info_requirement'),
    t('svhc_names'),
    t('covered_by_rohs_directive'),
  ].concat(attributesToHide),
);
const isWideTable = computed(() => sortedVariants.value.some((variant) => variant.webSalesUnit));
const isTableOverlayVisible = computed(
  () => !isExpanded.value && props.product.variants && props.product.variants?.length > previewLimit,
);
const getStoreName = computed(() => getStore(getCurrentStockLocation.value).value?.name || 'Butikslager');

const sortedTableHeaders = computed(() => {
  const headers =
    props.product.variantAttributes?.filter(
      (header: { name: string }) => !hiddenAttributes.value.includes(header.name),
    ) || [];

  if (brandConfig.additions.displaySizeDescriptionAsSecondAttribute) {
    const sizeDescriptionHeader = headers.find((h) => h.name === SIZE_DESCRIPTION_ATTRIBUTE_NAME);
    if (sizeDescriptionHeader) {
      // Give size description the lowest sort value because article number is always put first anyways
      sizeDescriptionHeader.sort = Number.MIN_SAFE_INTEGER;
    }
  }

  let sortedTableHeadersArray: VariantTableHeader[] = headers.sort((a: any, b: any) => {
    return parseInt(a.sort, 10) > parseInt(b.sort, 10) ? 1 : -1;
  });

  // If sequencing is set in AdminUI
  if (sortSequence.value?.length > 0) {
    sortedTableHeadersArray = sortAttributesWithSequencing(sortedTableHeadersArray, sortSequence.value, 'name');
  }

  // Remove art nr, since it always gonna show. We use it as a fixed col (see const artNrColumnHeader)
  const findArtNrIdx = sortedTableHeadersArray.findIndex((header) => header.name === 'articleNumber');
  if (findArtNrIdx > -1) {
    sortedTableHeadersArray.splice(findArtNrIdx, 1);
  }

  if (brandConfig.additions.showVariantNameInTable) {
    sortedTableHeadersArray.unshift({
      label: 'Art. namn.',
      name: variantNameAttribute,
      sort: '1',
    });
  }

  return sortedTableHeadersArray;
});

const getHeaderIndex = (attributeName: string): number => {
  const idx = sortedTableHeaders.value.findIndex((att) => `attributes-${att.name}` === attributeName);
  return idx;
};

const enrichedVariants = computed<SortVariant[]>(() => {
  const internalHiddenAttributes = [...hiddenAttributes.value, 'articleNumber'];
  const variants =
    props.product.variants?.map((variant: SortVariant) => ({
      ...variant,
      product: {
        name: props.product.name,
        id: props.product.id,
        breadcrumb: props.product.breadcrumb,
        categoryPaths: props.product.categoryPaths,
      },
    })) || [];
  // Loop over all of the variants and add a combined attributes string for sorting
  for (const variant of variants) {
    const attributesToCombine = [];

    // Loop through the sorted table headers and add the attribute value to the combined attributes
    // if we do not use sortedTableHeaders we can not guarantee that the attributes are sorted correctly
    for (const header of sortedTableHeaders.value) {
      if (brandConfig.additions.showVariantNameInTable && header.name === variantNameAttribute) {
        variant.attributes?.push({
          attributeName: header.name,
          attributeValue: variant.name!,
          attributeLabel: header.label,
          unit: '',
        });
      }

      const attribute = variant.attributes?.find((a: any) => a.attributeName === header.name);
      if (!attribute || internalHiddenAttributes.includes(attribute.attributeName)) {
        continue;
      }

      attributesToCombine.push((attribute.attributeName + attribute.attributeValue).replace(';', ''));
    }

    variant.combinedAttrs = attributesToCombine.join(';');
  }
  return variants;
});

const sortedVariants = computed(() => {
  let sort = (a: SortVariant, b: SortVariant) =>
    sortAttributes(sortBy.value(a), sortBy.value(b), isSortAcsending.value);
  const variants = [...enrichedVariants.value];

  const headerIndex = getHeaderIndex(sortingId.value);
  // By default we are sorting on all attributes after the sorted column, except article number/price/stock
  if (!sortBy.value || headerIndex >= 0) {
    // Set the sorting function to sort by the combined attributes
    sort = (a, b) =>
      sortAttributes(
        getCombinedAttributesByIndex(a.combinedAttrs, !sortBy.value ? 0 : headerIndex),
        getCombinedAttributesByIndex(b.combinedAttrs, !sortBy.value ? 0 : headerIndex),
        isSortAcsending.value,
        false,
      );
  }

  const rv = variants.sort(sort).map((variant) => {
    const { combinedAttrs, ...rest } = variant;
    return rest;
  });

  if (limit.value) {
    return rv.slice(0, limit.value);
  }

  return rv;
});

const getCombinedAttributesByIndex = (fullCombinedAttributes: string, index: number): string => {
  return fullCombinedAttributes.split(';').slice(index).join(';');
};

const isAnyVariantDiscontinued = computed(() => {
  return sortedVariants.value.some((variant) => variant.discontinued);
});

const isCounterVisible = computed(() => {
  return props.layout === 'plp' ? 'counter-button-class lg:hidden' : 'hidden';
});

provide('counterButtonClass', isCounterVisible);

const trackVisibleVariants = () => {
  if (isTrackingComplete.value) return;
  let variants = sortedVariants.value.map((variant, index) => ({ ...variant, listPosition: ++index }));
  if (variants.length === 10 && props.product.variants && props.product.variants.length > variants.length) {
    variants = variants.slice(0, 7);
  } else if (variants.length > 10) {
    variants = variants.slice(7);
  }
  $tracking.emit('variantImpression', {
    variants,
    list: config.public.gtm.PRODUCT_VARIANTS_LIST,
  });

  isTrackingComplete.value = sortedVariants.value.length === props.product.variants?.length;
};

const getLocation = (variant: Variant, locationId?: string): { quantity?: number } => {
  return variant?.stock?.locations?.find((location) => location.locationId === locationId) || {};
};

const updateSortingConfig = (propertyName: string, key?: string) => {
  // Create an id to keep track of the latest performed sort
  const newSortingId = `${propertyName}-${key}`;
  isSortAcsending.value = newSortingId === sortingId.value ? !isSortAcsending.value : true;
  sortingId.value = newSortingId;

  switch (propertyName) {
    case 'attributes':
      sortBy.value = (variant: Variant) =>
        variant.attributes?.find((attribute) => attribute.attributeName === key)?.attributeValue || '';
      break;
    case 'centralStock':
      sortBy.value = (variant: Variant) => getLocation(variant, centralLocationId.value)?.quantity?.toString() || '';
      break;
    case 'storeStock':
      sortBy.value = (variant: Variant) =>
        getLocation(variant, getCurrentStockLocation.value)?.quantity?.toString() || '';
      break;
    case 'price':
      sortBy.value = (variant: Variant) => {
        const multiplier = variant.webSalesUnit?.conversionFactor || 1;
        return variant.price?.netPrice ? (parseFloat(variant.price.netPrice) * multiplier).toString() : '';
      };
      break;
  }
};

const setStockInformationData = (type: string | null, variant?: Variant) => {
  if (!type || !variant) {
    stockInformationData.value = null;
    return;
  }

  type Payload = { data: Variant; type: string; isPurchasable?: { store: boolean; online: boolean } };
  const payload: Payload = {
    data: variant,
    type,
  };
  const isPurchasable = isPurchasableOnline(payload.data, getCurrentStockLocation.value);
  payload.isPurchasable = isPurchasable;
  if (!isPurchasable.online) payload.type = 'global_stock_status';

  stockInformationData.value = payload;
};

const setIsOverflowing = () => {
  if (variantsTable.value) {
    const { offsetWidth, scrollWidth } = variantsTable.value || {};
    isOverflowing.value = offsetWidth < scrollWidth;
  }
};

const onScroll = () => {
  if (variantsTable.value) {
    const { offsetWidth, scrollLeft, scrollWidth } = variantsTable.value || {};
    const scrollPosition = scrollLeft + offsetWidth;
    isScrolled.value = scrollLeft > 0;
    isScrolledToEnd.value = scrollWidth - scrollPosition < 1;
  }
};

const closePriceAndDiscountModal = () => {
  isPriceAndDiscountModalVisible.value = false;
  selectedVariant.value = null;
};

const openPriceModal = (variant: Variant) => {
  selectedVariant.value = variant;
  isPriceAndDiscountModalVisible.value = true;
};

const openReplacementModal = (variant: Variant) => {
  selectedVariant.value = variant;
  discontinuedArticleNumber.value = variant.articleNumber;
};

const closeReplacementModal = () => {
  selectedVariant.value = null;
  discontinuedArticleNumber.value = undefined;
};

const expandVariants = () => {
  isExpanded.value = true;
  limit.value = props.product.variants?.length || 10;
};

const showMoreVariants = () => {
  limit.value += SHOW_MORE_NUM;
};

const trackNavigation = (articleNumber?: string) => {
  visitProduct(articleNumber);
};

const onVariantTableUpdated = () => {
  emit('variant-table-updated', sortedVariants.value);
  setIsOverflowing();
};

onUpdated(() => {
  onVariantTableUpdated();
});

onMounted(() => {
  onVariantTableUpdated();
  window.addEventListener('resize', setIsOverflowing);
  variantsTable.value?.addEventListener('scroll', onScroll);
  if (!isLoggedIn.value) {
    trackVisibleVariants();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setIsOverflowing);
  variantsTable.value?.removeEventListener('scroll', onScroll);
});

watch(
  () => isLoading.value.price,
  (newValue, oldValue) => {
    if (newValue === false && oldValue === true) {
      trackVisibleVariants();
    }
  },
);

watch(limit, () => {
  trackVisibleVariants();
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'VariantTable',
});
</script>

<style lang="scss" scoped>
$breakpoint: 56rem;
.variant-table-grid-layout {
  --cols: 1;
  @apply grid w-full relative text-xs;
  @apply @4xl:grid-rows-[47px] @4xl:gap-0;
  &:not(.no-cols) {
    @apply @4xl:overflow-x-auto @4xl:overflow-y-hidden;
  }

  &.full-variant {
    &:not(.no-cols) {
      @container (min-width: #{$breakpoint}) {
        grid-template-columns: auto repeat(var(--cols), auto) 1fr auto;
      }
    }
    &.no-cols {
      @apply @4xl:grid-cols-[auto_1fr_auto];
    }
  }
  &:not(.full-variant) {
    &:not(.no-cols) {
      @container (min-width: #{$breakpoint}) {
        grid-template-columns: auto repeat(var(--cols), auto) 1fr;
      }
    }
    &.no-cols {
      @apply @4xl:grid-cols-[auto_1fr];
    }
  }
}
</style>
